import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

export default function ServicioB2B() {
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>Servicio B2B | Innotec Food Equipment</title>
        <meta name="description" content="Servicio de gestión B2B de Innotec Food Equipment que interconecta empresas en todo el mundo para la compra-venta de maquinaria de alimentación." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-[75vh] flex items-center">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0B] to-transparent z-10"></div>
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1551632436-cbf8dd35adfa?ixlib=rb-4.0.3&auto=format&fit=crop&q=80&w=2070')] bg-cover bg-center bg-fixed z-0"></div>
          <video
            className="w-full h-full object-cover z-5 opacity-70"
            autoPlay
            muted
            loop
            playsInline
          >
            <source src="https://cdn.coverr.co/videos/coverr-businessmen-shaking-hands-5585/1080p.mp4" type="video/mp4" />
          </video>
        </div>

        <div className="container mx-auto px-6 relative z-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-3 py-1 text-sm bg-[#FCAA0D] text-[#0A0A0B] rounded-full mb-6">
              Nueva Modalidad
            </span>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Servicio de gestión <span className="text-[#FCAA0D]">B2B</span>
            </h1>
            <p className="text-xl text-gray-200 mb-8">
              Presentamos la nueva modalidad de compra de maquinaria de alimentación de Innotec Food,
              que interconecta empresas en todo el mundo: el Servicio B2B.
            </p>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 p-6 rounded-xl mb-8">
              <p className="text-2xl font-medium text-white italic">
                "Lo que uno no utiliza, <span className="text-[#FCAA0D]">otro lo necesita</span>"
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Descripción */}
      <section className="py-20 bg-[#0A0A0B]">
        <div className="container mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16"
          >
            <motion.div
              variants={fadeInUp}
              className="bg-[#0A0A0B]/80 backdrop-blur-sm border border-white/10 rounded-xl p-8 hover:shadow-[0_0_30px_rgba(252,170,13,0.2)] hover:border-[#FCAA0D]/30 transition-all duration-500"
            >
              <p className="text-lg text-white/80 leading-relaxed">
                Esta fórmula que INNOTEC pone a disposición de sus clientes, prácticamente sin costes añadidos,
                pretende poner en contacto a fabricantes que quieran vender su maquinaria porque no la están utilizando,
                con empresas que lo necesitan.
              </p>
              <p className="text-lg text-white/80 leading-relaxed mt-4">
                Innotec tendrá el único cometido de conectarlas entre sí. Ser el punto de encuentro para
                alcanzar el acuerdo comercial más beneficioso para ambos.
              </p>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="flex items-center justify-center"
            >
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&auto=format&fit=crop&q=80&w=1170"
                alt="Conectando empresas"
                className="rounded-xl shadow-lg max-h-[300px] object-cover"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Cómo funciona */}
      <section className="py-20 bg-gradient-to-b from-[#0A0A0B] to-[#0A0A0B]">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-5xl mx-auto mb-16"
          >
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-8 text-center">
              ¿CÓMO <span className="text-[#FCAA0D]">FUNCIONA</span>?
            </h3>

            <div className="grid md:grid-cols-3 gap-6">
              <motion.div
                whileHover={{ y: -10 }}
                transition={{ duration: 0.3 }}
                className="bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm border border-white/10 rounded-xl p-6 flex flex-col items-center text-center hover:shadow-[0_0_20px_rgba(252,170,13,0.2)] hover:border-[#FCAA0D]/20 transition-all"
              >
                <div className="h-14 w-14 rounded-full bg-[#FCAA0D] flex items-center justify-center mb-4 text-[#0A0A0B] font-bold text-xl">
                  1
                </div>
                <p className="text-white/80">
                  Si eres una empresa y tienes maquinaria industrial alimentaria que ya no uses o preveas no usar,
                  sólo tienes que introducir los datos en el formulario. ¡Sé rápido, las plazas son limitadas!
                </p>
              </motion.div>

              <motion.div
                whileHover={{ y: -10 }}
                transition={{ duration: 0.3 }}
                className="bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm border border-white/10 rounded-xl p-6 flex flex-col items-center text-center hover:shadow-[0_0_20px_rgba(252,170,13,0.2)] hover:border-[#FCAA0D]/20 transition-all"
              >
                <div className="h-14 w-14 rounded-full bg-[#FCAA0D] flex items-center justify-center mb-4 text-[#0A0A0B] font-bold text-xl">
                  2
                </div>
                <p className="text-white/80">
                  Te solicitaremos datos para tener una visión perfecta de tu máquina. Ten en cuenta que el vendedor
                  puede estar al otro lado del mundo y necesitará detalles completos.
                </p>
              </motion.div>

              <motion.div
                whileHover={{ y: -10 }}
                transition={{ duration: 0.3 }}
                className="bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm border border-white/10 rounded-xl p-6 flex flex-col items-center text-center hover:shadow-[0_0_20px_rgba(252,170,13,0.2)] hover:border-[#FCAA0D]/20 transition-all"
              >
                <div className="h-14 w-14 rounded-full bg-[#FCAA0D] flex items-center justify-center mb-4 text-[#0A0A0B] font-bold text-xl">
                  3
                </div>
                <p className="text-white/80">
                  En el formulario deberás marcar el precio que quieres por tu equipo. El comprador podrá hacer una contraoferta,
                  pero la decisión final será exclusivamente del vendedor.
                </p>
              </motion.div>
            </div>
          </motion.div>

          <div className="max-w-4xl mx-auto mb-8 p-6 bg-white/5 border border-white/20 rounded-xl">
            <p className="text-white/80 text-lg">
              El importe de las compras será depositado de forma segura bajo nuestra tutela hasta que el comprador
              reciba la máquina en sus instalaciones y verifique, en un plazo máximo de 48 horas, que todo está
              en orden y el funcionamiento corresponde con lo pactado.
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-5xl mx-auto text-center mb-16"
          >
            <h3 className="text-2xl font-bold text-white mb-6">
              InnotecFood <span className="text-[#FCAA0D]">Connecting business</span>
            </h3>
            <p className="text-white/80 mb-8">
              Puedes ver nuestros vídeos explicativos para conocer mejor el servicio. Todo lo que necesitas saber sobre
              las distintas modalidades B2B lo encontrarás en las condiciones Especiales B2B Innotec.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {[
                "Bienvenido a B2B de Innotec",
                "Servicio B2B de Innotec",
                "Presentación B2B Innotec Food",
                "Conoce B2B Innotec",
                "¿Cómo comprar Maquinaria en Innotecfood?",
                "¿Cómo funciona el sistema de cupos?"
              ].map((title, index) => (
                <motion.a
                  key={index}
                  whileHover={{ y: -5, scale: 1.05 }}
                  className="bg-white/5 border border-white/20 p-4 rounded-lg hover:bg-[#FCAA0D]/10 hover:border-[#FCAA0D]/30 transition-all flex items-center justify-center text-center text-white/80 hover:text-white"
                  href="#"
                >
                  {title}
                </motion.a>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* B2B ORO */}
      <section className="py-20 bg-[url('https://images.unsplash.com/photo-1551632436-cbf8dd35adfa?ixlib=rb-4.0.3&auto=format&fit=crop&q=80&w=2070')] bg-cover bg-center bg-fixed relative">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0B]/90 via-[#0A0A0B]/70 to-[#0A0A0B]/90"></div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-5xl mx-auto bg-[#FCAA0D]/10 backdrop-blur-sm border border-[#FCAA0D]/30 rounded-xl p-8 hover:shadow-[0_0_30px_rgba(252,170,13,0.3)] transition-all mb-16"
          >
            <h3 className="text-3xl font-bold text-[#FCAA0D] mb-6 text-center">
              B2B <span className="text-white">ORO</span>
            </h3>
            <p className="text-white/80 text-lg mb-6">
              Para aquellas empresas que busquen un servicio integral tenemos a su disposición la modalidad B2B ORO de Innotec.
            </p>
            <p className="text-white/80 mb-6">
              Ahora no sólo te pondremos en contacto con potenciales compradores de todo el mundo, sino que también:
            </p>
            <ul className="space-y-3 mb-6">
              {[
                "Recogeremos tu maquinaria",
                "La custodiaremos en nuestras instalaciones",
                "La limpiaremos en profundidad",
                "El Servicio Técnico Oficial de la marca revisará y reparará todo lo necesario",
                "Después de abrillantarla, haremos fotografías y vídeos profesionales",
                "La publicaremos en nuestra web e informaremos a todos nuestros colaboradores"
              ].map((item, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start"
                >
                  <span className="text-[#FCAA0D] mr-2">✓</span>
                  <span className="text-white/80">{item}</span>
                </motion.li>
              ))}
            </ul>
            <p className="text-xl text-white text-center font-medium">
              Y todo esto, <span className="text-[#FCAA0D]">¡sin costes adicionales al servicio!</span>
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center mb-16"
          >
            <h3 className="text-2xl font-bold text-white mb-6">
              No te quedes sin tu <span className="text-[#FCAA0D]">plaza</span>
            </h3>
            <p className="text-lg text-white/80 mb-8">
              El Servicio B2B Innotec es un servicio exclusivo para los clientes que lo soliciten dentro
              de un plazo estipulado. Es decir, funciona en modalidad de <span className="text-[#FCAA0D] font-medium">CUPOS</span>.
            </p>
            <p className="text-lg text-white/80 mb-8">
              Tras revisar toda la documentación que nos envíes sobre tus equipos y su situación,
              nuestro departamento encargado lo estudiará y revisará en profundidad para determinar
              si cumples los requisitos. Si es así, serás incluido en el programa exclusivo B2B Innotec.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col md:flex-row justify-center space-y-4 md:space-y-0 md:space-x-6"
          >
            <motion.a
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
              href="/comprador"
              className="bg-white px-8 py-4 rounded-lg text-[#0A0A0B] font-bold text-center shadow-lg flex items-center justify-center space-x-2 hover:shadow-[0_0_20px_rgba(255,255,255,0.4)] transition-all"
            >
              <span>Soy Comprador</span>
              <span className="text-sm bg-[#FCAA0D] px-2 py-1 rounded">Acceder al listado de stock</span>
            </motion.a>
            <motion.a
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
              href="/vendedor"
              className="bg-[#FCAA0D] px-8 py-4 rounded-lg text-[#0A0A0B] font-bold text-center shadow-lg flex items-center justify-center space-x-2 hover:shadow-[0_0_20px_rgba(252,170,13,0.4)] transition-all"
            >
              <span>Soy Vendedor</span>
              <span className="text-sm bg-white px-2 py-1 rounded">Cumplimentar formulario</span>
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-gradient-to-b from-[#0A0A0B] to-black relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&q=80&w=2070')] bg-cover bg-center opacity-20"></div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-8 leading-tight">
              Únete al <span className="text-[#FCAA0D]">servicio B2B</span> de Innotec
            </h2>
            <p className="text-xl text-white/70 mb-10 max-w-3xl mx-auto">
              Conectamos empresas de todo el mundo para facilitar la compra-venta de maquinaria de alimentación,
              garantizando transacciones seguras y beneficiosas para todas las partes.
            </p>
            <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/20 mb-12 max-w-2xl mx-auto shadow-[0_0_20px_rgba(252,170,13,0.1)]">
              <p className="text-2xl font-semibold text-white">
                ¿Quieres formar parte? Contacta con nosotros <span className="text-[#FCAA0D]">AHORA</span>
              </p>
            </div>
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-6 justify-center">
              <a
                href="/contacto"
                className="bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-[#0A0A0B] font-medium py-4 px-8 rounded-lg transition-all transform hover:scale-105 shadow-[0_0_15px_rgba(252,170,13,0.3)]"
              >
                Solicitar información
              </a>
              <a
                href="/formulario-b2b"
                className="bg-white/5 border border-white/20 text-white hover:border-[#FCAA0D] hover:shadow-[0_0_15px_rgba(252,170,13,0.2)] font-medium py-4 px-8 rounded-lg transition-all"
              >
                Acceder al formulario
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
